import { useAutoneTranslation } from '@autone/translations';
import { useMemo } from 'react';

import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from '../../../../atoms';
import { cn } from '../../../../utils';
import { useAdvancedFiltersInternalContext } from '../contexts/advanced-filters-internal.context';

import { AdvancedFiltersFieldOption } from './AdvancedFiltersFieldOption';

interface AdvancedFiltersFieldSelectProps {
  rowId: string;
}

export const AdvancedFiltersFieldSelect = ({
  rowId,
}: AdvancedFiltersFieldSelectProps) => {
  const { common } = useAutoneTranslation();
  const { state, dispatch, config } = useAdvancedFiltersInternalContext();

  const { field } = state.conditions[rowId];
  const { fieldOptions, renderFieldOptions } = config;

  const handleUpdateField = (field: string | undefined) => {
    dispatch({
      type: 'PATCH_CONDITION',
      payload: {
        rowId,
        condition: {
          field,
          // always reset value because fields could be incompatible
          value: undefined,
        },
      },
    });
  };

  const fieldOptionItems = useMemo(() => {
    return Object.fromEntries(
      Object.entries(fieldOptions).map(([field, translation]) => [
        field,
        <AdvancedFiltersFieldOption.Item key={field} value={field}>
          {translation}
        </AdvancedFiltersFieldOption.Item>,
      ]),
    );
  }, [fieldOptions]);

  return (
    <Select value={field} onValueChange={handleUpdateField}>
      <div className="border-r border-border">
        <SelectTrigger
          aria-label="Select advanced filter"
          id="select-advanced-filter"
          data-testid="select-advanced-filter"
          className={cn(
            'focus-within:ring-0 focus-visible:outline-none focus-within:outline-none focus-visible:ring-0',
            'w-full h-full rounded-none border-none',
          )}
        >
          <div className="w-full text-start overflow-hidden text-ellipsis text-sm">
            <SelectValue placeholder={common('advanced-filters.column')} />
          </div>
        </SelectTrigger>
      </div>
      <SelectContent data-testid="advanced-filters-column-select-content">
        {renderFieldOptions
          ? renderFieldOptions({ fieldOptionItems })
          : Object.values(fieldOptionItems)}
      </SelectContent>
    </Select>
  );
};
